
import { Component, OnDestroy, TemplateRef, ViewChild, Injector, Input } from '@angular/core';

import { BehaviorSubject, Subscription, filter } from 'rxjs';

import { InteractionModel } from '../../../../Discover/Targeting/models/interaction-model';
import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { InteractionViewService } from '../../services/interaction-view.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AddInteractionDialogComponent } from './AddInteractionDialog/add-interaction-dialog.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { EmailConversationService } from '../../../../Discover/Targeting/services/email-conversation.service';
import { RepliesSearchApi } from '../../../../api/targeting/model/replies-search-api';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { BaseJournodexEntityModel } from '../../../../Discover/Targeting/models/base-journodex-entity.model';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { EmploymentModel } from '../../../../Discover/Targeting/models/employment.model';
import { TargetingSearchService } from '@Discover/Targeting/services/targeting-search.service';
import { animate, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { toolbarAnimation } from '@Common/Misc/animations/toolbar-animation';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';


@Component({
    selector: 'agpr-interaction-view-tab',
    templateUrl: './interaction-view-tab.component.html',
    styleUrls: ['./interaction-view-tab.component.scss'],
    providers: [
        ToolbarStateService,
        {
            provide: TabComponent,
            useExisting: InteractionViewTabComponent
        },
        {
            provide: FilterService,
            useExisting: InteractionViewService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: InteractionViewService
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: InteractionViewService
        },
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        toolbarAnimation
    ],
    standalone: false
})
export class InteractionViewTabComponent extends TabComponent implements OnDestroy {

    @Input() entity: BaseJournodexEntityModel;
    @Input() contactPrivateData: PrivateDataModel;
    @Input() outletPrivateData: PrivateDataModel;
    @Input() selectedEmploymentChanged: EmploymentModel;
    @Input() hideBusinessCard: boolean = false;

    interactions: InteractionModel[] = [];
    entityType: EntityType;
    entityId: string;
    total: number = 0;
    loading: boolean = false;
    subscriptions: Subscription[] = [];
    chipsVisible: boolean = false;
    overlayContainer: Element;

    @ViewChild('tabHeader') declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('tabFooter') declare footer: TemplateRef<any>;

    id: string = 'interactionView';
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        public interactionViewService: InteractionViewService,
        public usersService: UsersService,
        public coreDialogService: CoreDialogService,
        protected currentUserStateService: CurrentUserStateService,
        private emailConversationService: EmailConversationService,
        public targetingSearchService: TargetingSearchService,
        private injector: Injector,
        private userPreferencesService: UserPreferencesService,
        private toolbarStateService: ToolbarStateService
    ){
        super();
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.interactionViewService.getDisplayInteractions().pipe(filter(x => !!x)).subscribe(interactions => {
                this.interactions = interactions;
            }),
            this.interactionViewService.getItemCount().subscribe(x => this.total = x),
            this.interactionViewService.getEntityType().pipe(filter(x => !!x)).subscribe(entityType => {
                this.entityType = entityType;
            }),
            this.interactionViewService.getEntityId().pipe(filter(x => !!x)).subscribe(entityId => this.entityId = entityId),
            this.interactionViewService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(chipFilter => chipFilter.chips && chipFilter.chips.length > 0);
            }),
            this.interactionViewService.getLoading().subscribe(loading => this.loading = loading),
            this.userPreferencesService.getUserAppPreferences().subscribe(prefs => {
                this.toolbarStateService.toolbarAnimationDisabled = !prefs.generalPrefs.accessibility.collapseToolbars;
            })
        );
    }

    openAddInteractionDialog(interaction?: InteractionModel) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(AddInteractionDialogComponent, {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                setInteraction: interaction ? interaction : null,
                entityType: this.entityType,
                entityId: this.entityId,
            },
            outputs: {
                dialogClose: (updateList: boolean) => {
                    if (updateList) {
                        this.interactionViewService.getInteractionsFromApi();
                    }
                }
            }
        }, this.injector);
    }

    viewConversation(interaction: InteractionModel): void {
        this.emailConversationService.viewConversation({
            distributionId: interaction.distId,
            grantId: interaction.sendFrom.grantId
        } as RepliesSearchApi, interaction.contactId);
    }

    /**
     * Delegates scroll updates to the ToolbarStateService, which handles
     * collapsing or expanding the toolbar based on scroll movement.
     *
     * @param scrollTop - The current vertical scroll position of the container.
     */
    handleScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }

    /**
     * Triggered when the toolbar animation starts.
     */
    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    /**
     * Triggered when the toolbar animation ends.
     */
    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
